import React, { useState, MutableRefObject } from 'react'
import { useParams } from 'react-router-dom'

import {
  Dropdown,
  useModalContext,
  SelectDropdownActionDropdown,
} from '@sketch/components'
import {
  useFlag,
  ShareDropPayload,
  DroppableLinkItem,
  RouteParams,
} from '@sketch/modules-common'
import { ConfirmMoveModal } from 'modules/projects/modals'

import { ProjectLink } from '../../types'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { RenameInlineEditor } from '../InlineEditor'
import {
  CopyProjectAction,
  DeleteProjectAction,
  LeaveProjectAction,
  RenameProjectAction,
  ShareProjectAction,
  CreateCollectionAction,
} from '../ProjectDropdownActions'

import { useRemoveSharesFromCollection } from 'modules/collections/operations'
import { useToast } from '@sketch/toasts'

import { ReactComponent as FolderClosedIcon } from '@sketch/icons/folder-closed-16'
import { ReactComponent as FolderOpenIcon } from '@sketch/icons/folder-open-16'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-16'
import { ReactComponent as PeopleIcon } from '@sketch/icons/persons-two-16'

export interface ProjectProps extends ProjectLink {
  userCanEdit: boolean
  userCanAdminister: boolean
  workspace: WorkspaceMinimalFragment
  refToLast?: React.MutableRefObject<HTMLElement>
  setEditedItem?: (value: string) => void
  autofocusLastCreated?: MutableRefObject<boolean>
  forceHighlight?: boolean
}

export const privacyIcon = (
  project: ProjectProps['project'],
  active: boolean = false
): React.ElementType => {
  switch (project.privacyIcon) {
    case 'GUESTS':
      return PeopleIcon

    case 'INVITED_WORKSPACE_MEMBERS':
      return LockIcon

    case 'WORKSPACE_MEMBERS':
      if (active) {
        return FolderOpenIcon
      } else {
        return FolderClosedIcon
      }
  }
}

export const ProjectListItem: React.FC<ProjectProps> = props => {
  const {
    link,
    project,
    userCanEdit,
    userCanAdminister,
    workspace,
    refToLast,
    setEditedItem,
    forceHighlight = false,
  } = props
  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const [isRenaming, setIsRenaming] = useState(false)
  const hasGuestsInProjects = useFlag('guests-in-project')
  const { collectionId: originCollectionId } = useParams<
    RouteParams<'WORKSPACE_COLLECTION'>
  >()

  const removeSharesFromCollection = useRemoveSharesFromCollection({
    projectIdentifier: project.identifier,
    onCompleted: () => {
      showToast(`Document removed from this collection`)
    },
  })

  const onDropped = ({ share }: ShareDropPayload) => {
    if (!share) {
      return
    }

    // if we're moving this share to the same project + we're also coming from a collection
    // then what we actually want is to remove this share from this collection!
    if (
      share.project?.identifier === project.identifier &&
      originCollectionId
    ) {
      removeSharesFromCollection({
        variables: {
          identifier: originCollectionId,
          shareIdentifiers: [share.identifier],
        },
      })

      return
    }

    showModal(ConfirmMoveModal, {
      share,
      newProject: project,
      // Warn the user the document will become public
      isDraftDocument: share.project?.type === 'PERSONAL_DRAFTS',
    })
  }

  // only editors and admins can perform the following actions:
  //   - Add/remove people from the project (<ShareProjectAction .../>)
  //   - Rename the project (<RenameProjectAction .../>)
  //   - Delete the project (<DeleteProjectAction .../>)
  const showEditActions = userCanEdit || userCanAdminister
  // The `Leave...` button is being added as part of the guests-in-projects feature,
  // only users with the feature flag enabled AND who can leave the project
  // should see the button.
  const showLeaveAction = hasGuestsInProjects && project.userCanLeave
  const showDivider = showEditActions || showLeaveAction

  const isCollectionsFFEnabled = useFlag('collections')

  const workspaceId = workspace.identifier

  const dropdownContent = (
    <>
      <Dropdown.Header>Project</Dropdown.Header>
      {showEditActions && (
        <ShareProjectAction project={project} workspaceId={workspaceId} />
      )}
      {showEditActions && (
        <RenameProjectAction onRename={() => setIsRenaming(!isRenaming)} />
      )}
      {showEditActions && isCollectionsFFEnabled && (
        <CreateCollectionAction
          project={project}
          workspaceIdentifier={workspaceId}
        />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {showDivider && <Dropdown.Divider />}
      {showLeaveAction && (
        <LeaveProjectAction
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem?.(project.identifier)
  }

  if (isRenaming) {
    return (
      <RenameInlineEditor
        projectId={project.identifier}
        projectName={project.name}
        workspaceId={workspaceId}
        placeholder={project.name || 'Folder'}
        toggleEditor={toggleEditor}
        onCompleted={onCompleted}
      />
    )
  }

  return (
    <DroppableLinkItem
      icon={{
        icon: privacyIcon(project),
        activeIcon: privacyIcon(project, true),
      }}
      to={link}
      text={project.name}
      onDropped={onDropped}
      action={
        dropdownContent && (
          <SelectDropdownActionDropdown
            text="Rename & More…"
            content={dropdownContent}
          />
        )
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
      forceHighlight={forceHighlight}
    />
  )
}
